import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Lightning, Package, Warehouse, Receipt, ShoppingCart } from "@phosphor-icons/react";

/**
 * MaterialAutocomplete — smart search input that pulls from the unified
 * /api/suggestions/materials endpoint. On selection, calls onPick(material)
 * so the parent can auto-fill sku, unit, hsn, gst_rate, last_unit_price.
 */
const SOURCE_ICON = { site: Package, warehouse: Warehouse, purchase: ShoppingCart, invoice: Receipt };
const SOURCE_LABEL = { site: "Site", warehouse: "WH", purchase: "Bill", invoice: "Inv" };

export default function MaterialAutocomplete({ value, onChange, onPick, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 1) { setRows([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/suggestions/materials?q=${encodeURIComponent(q)}&limit=25`);
        setRows(data?.materials || []);
        setActiveIdx(0);
      } finally { setLoading(false); }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  const pick = (m) => {
    onPick?.(m);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || !rows.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % rows.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i - 1 + rows.length) % rows.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(rows[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={value || ""}
        onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || "Type material name…"}
        data-testid={testid}
        className="h-7 text-[12.5px]"
        autoComplete="off"
      />
      {open && rows.length > 0 && (
        <div
          data-testid={`${testid}-popover`}
          className="absolute z-[60] top-full left-0 right-0 mt-1 max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg"
        >
          {loading && <div className="px-3 py-2 text-[11.5px] text-slate-400">Searching…</div>}
          {rows.map((m, i) => (
            <button
              key={`${m.description}-${m.sku}-${i}`}
              type="button"
              onClick={() => pick(m)}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`${testid}-opt-${i}`}
              className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-center gap-3 transition-colors ${i === activeIdx ? "bg-orange-50" : "hover:bg-slate-50"}`}
            >
              <Lightning size={12} className={i === activeIdx ? "text-[#EA580C]" : "text-slate-400"} weight={i === activeIdx ? "fill" : "regular"} />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-medium text-slate-900 truncate">{m.description}</div>
                <div className="text-[10.5px] text-slate-500 flex items-center gap-2 mt-0.5">
                  {m.sku && <span className="font-mono">{m.sku}</span>}
                  {m.hsn && <span>HSN {m.hsn}</span>}
                  {m.gst_rate > 0 && <span>{m.gst_rate}% GST</span>}
                  {m.last_unit_price > 0 && <span className="text-slate-700 font-mono-num">₹{Number(m.last_unit_price).toFixed(0)}</span>}
                  {(m.site_stock > 0 || m.warehouse_stock > 0) && (
                    <span className="text-emerald-700 font-mono-num">
                      Stock: {m.warehouse_stock > 0 && `WH ${m.warehouse_stock}`}{m.warehouse_stock > 0 && m.site_stock > 0 && " · "}{m.site_stock > 0 && `Site ${m.site_stock}`}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                {(m.sources || []).map((s) => {
                  const Ic = SOURCE_ICON[s] || Package;
                  return (
                    <span key={s} title={SOURCE_LABEL[s]} className="inline-flex items-center rounded-sm bg-slate-100 text-slate-600 border border-slate-200 px-1 py-[1px] text-[9px] font-semibold uppercase tracking-widest gap-0.5">
                      <Ic size={9} />
                      {SOURCE_LABEL[s]}
                    </span>
                  );
                })}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
